import { useEffect, useRef, useState } from 'react'
import {
  CustomTable,
  CustomTableRef,
  stringFilterTemplate,
  DateFilterTemplate,
} from 'medere-ui-framework'
import { EStudyInformStatus } from '../../enums'
import {
  getAllStudiesInform,
  getStudyTypes,
  revertStudyInformToPending,
  exportStudiesInform,
} from '../../../../services/StudiesInformController'
import { StudiesInformListItemDTO } from '../../../../dto/StudiesInformListItemDTO'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { ColumnFilterElementTemplateOptions } from 'primereact/column'
import RevertToPendingModal from './RevertToPendingModal'
import CancelReasonModal from './CancelReasonModal'
import { formatDateWithTime } from '../../helpers'

function CancelledCard({ entity, btn1 }: { entity: StudiesInformListItemDTO; btn1?: any }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
      <div>
        <strong>{entity.patientFullName}</strong>
        <p style={{ margin: 0 }}>{entity.studyTypeLabel} — {entity.requestedDate}</p>
        <p style={{ margin: 0, color: '#888' }}>{entity.professionalFullName} | {entity.location}</p>
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {btn1 && <Button className={btn1.className} icon={"pi " + btn1.icon} onClick={btn1.onClick} />}
      </div>
    </div>
  )
}

export default function CancelledView() {
  const tableRef = useRef<CustomTableRef>(null)
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [typeOptions, setTypeOptions] = useState<Array<{ label: string; value: string }>>([])
  const [revertModalVisible, setRevertModalVisible] = useState(false)
  const [revertTargetId, setRevertTargetId] = useState<number | null>(null)
  const [revertLoading, setRevertLoading] = useState(false)
  const [reasonModalVisible, setReasonModalVisible] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    getStudyTypes().then(setTypeOptions).catch(() => setTypeOptions([]))
  }, [])

  function openRevertModal(item: StudiesInformListItemDTO) {
    setRevertTargetId(item.studyDiagnosticId)
    setRevertModalVisible(true)
  }

  function openReasonModal(item: StudiesInformListItemDTO) {
    setReason(item.cancelReason ?? '')
    setReasonModalVisible(true)
  }

  function handleRevertConfirm() {
    if (revertTargetId == null) return
    setRevertLoading(true)
    revertStudyInformToPending(revertTargetId)
      .then(() => {
        setRevertModalVisible(false)
        setRevertTargetId(null)
        tableRef.current?.refreshTable?.()
      })
      .finally(() => setRevertLoading(false))
  }

  return (
    <>
    <RevertToPendingModal
      visible={revertModalVisible}
      onHide={() => { setRevertModalVisible(false); setRevertTargetId(null) }}
      onConfirm={handleRevertConfirm}
      loading={revertLoading}
    />
    <CancelReasonModal
      visible={reasonModalVisible}
      onHide={() => setReasonModalVisible(false)}
      reason={reason}
    />
    <div className='viewContainer studies-inform-view cancelled-view'>
      <CustomTable
        idTable='StudiesInformCancelledTable'
        className='studies-inform-table'
        ref={tableRef}
        getItems={getAllStudiesInform}
        params={{ studyInformStatus: EStudyInformStatus.SD_CANCELED, typeFilter }}
        resizableColumns
        dynamicBehaviour
        CardTemplate={CancelledCard}
        cardKeyName='studyDiagnosticId'
        filterRow
        hideFiltersButton
        headerClassName='studies-inform-header'
        refreshButton
        exportItems={[{ label: 'Excel', icon: 'pi-file-excel', onExport: exportStudiesInform }]}
        onClearFilters={() => setTypeFilter(null)}
      >
        <Column
          field='patientFullName'
          className='column'
          header='Paciente'
          filter
          filterElement={stringFilterTemplate}
          body={(row: StudiesInformListItemDTO) => (
            <div>
              <div>{row.patientFullName}</div>
              <div style={{ fontSize: '0.8em', color: '#888' }}>{row.patientFullDoc}</div>
            </div>
          )}
        />
        <Column
          field='servicedatetime'
          className='column'
          header='Fecha y hora'
          filter
          filterElement={DateFilterTemplate}
          body={(row: StudiesInformListItemDTO) => formatDateWithTime(row.serviceDate)}
          style={{ minWidth: '13rem' }}
        />
        <Column
          field='studyTypeLabel'
          className='column'
          header='Estudio'
          filter
          filterElement={(filter: ColumnFilterElementTemplateOptions) => (
            <Dropdown
              options={typeOptions}
              optionLabel='label'
              optionValue='value'
              value={typeFilter}
              showClear
              className='w-full'
              style={{ height: '2.25rem', marginBottom: '0.28rem' }}
              onChange={(e) => {
                const newValue = (e.value ?? null) as string | null
                setTypeFilter(newValue)
                filter.filterCallback(newValue)
              }}
            />
          )}
          style={{ minWidth: '10rem' }}
        />
        <Column
          field='professionalFullName'
          className='column'
          header='Medico Solicitante'
          filter
          filterElement={stringFilterTemplate}
        />
        <Column
          field='location'
          className='column'
          header='Locación'
          filter
          filterElement={stringFilterTemplate}
        />
        <Column
          className='column'
          filter
          filterElement={() => (
            <Button
              icon='pi pi-eraser'
              className='buttonControl'
              tooltip='Limpiar filtros'
              tooltipOptions={{ position: 'top' }}
              onClick={() => {
                tableRef.current?.internalClearFilters?.()
                setTypeFilter(null)
              }}
            />
          )}
          body={(row: StudiesInformListItemDTO) => (
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <Button className='buttonTable' icon='pi pi-replay' onClick={() => openRevertModal(row)} />
              {row.cancelReason && (
                <Button className='buttonTable' icon='pi pi-comment' onClick={() => openReasonModal(row)} />
              )}
            </div>
          )}
        />
      </CustomTable>
    </div>
    </>
  )
}
